import { formatNumber, parseInputNumber } from "./number-format.util"

/**
 * Parse a discount percentage from its input field, exactly as typed: "0,5"
 * is 0.5 %, "12,5" is 12.5 %. That is the number form--number-parser submits
 * and the server stores — nothing reads a value below 1 as a fraction, so the
 * preview must not either (it used to turn 0,5 % into 50 %).
 * @param {HTMLInputElement} input - The percentage input (its server-rendered value is a raw decimal)
 * @param {string} locale - The locale to use for parsing
 * @returns {number} - The percentage as typed; 0 when empty or not a number
 */
export function parseDiscountPercent(input, locale) {
  return parseInputNumber(input, locale) || 0
}

/**
 * Validate and clamp discount percentage to valid range (0-100)
 * Updates the input field if value is out of range
 * @param {HTMLElement} percentTarget - The percentage input element
 * @param {number} discountPercent - The percentage value to validate
 * @param {string} locale - The locale to use for formatting
 * @returns {number} - The validated and clamped percentage (0-100)
 */
export function validateAndClampPercent(percentTarget, discountPercent, locale) {
  if (discountPercent > 100) {
    discountPercent = 100
    percentTarget.value = formatNumber(100, locale, {
      style: "decimal",
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    })
  } else if (discountPercent < 0) {
    discountPercent = 0
    percentTarget.value = formatNumber(0, locale, {
      style: "decimal",
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    })
  }
  return discountPercent
}

/**
 * Format percentage field to zero
 * @param {HTMLElement} percentTarget - The percentage input element
 * @param {string} locale - The locale to use for formatting
 */
export function clearPercentField(percentTarget, locale) {
  if (percentTarget) {
    percentTarget.value = formatNumber(0, locale, {
      style: "decimal",
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    })
  }
}

/**
 * Get currency from document currency selector
 * @returns {string} - The currency code (default: "EUR")
 */
export function getCurrency() {
  const currencySelect = document.querySelector("#currency")
  return currencySelect?.value || "EUR"
}

/**
 * Determine whether a discount percentage greater than zero is present.
 * @param {HTMLElement} percentTarget - The percentage input element whose `value` will be parsed as a number.
 * @param {string} locale - The locale to use for parsing (e.g. "sk-SK").
 * @returns {boolean} `true` if `percentTarget` has a numeric value greater than 0, `false` otherwise.
 */
export function hasDiscount(percentTarget, locale) {
  if (!percentTarget?.value) return false
  return parseDiscountPercent(percentTarget, locale) > 0
}

/**
 * The lock a discount block puts on its [% | EUR] toggle in gross_per_rate, which takes
 * percentage discounts only (spec NG5): the document's block and a line's row do the same, and
 * each calls this from its syncGrossMode().
 *
 * While `gross` the EUR toggle is aria-disabled, not disabled: a disabled button leaves the tab
 * order, so keyboard and screen-reader users never reach why, and touch users never see a title.
 * It stays focusable, and the caller's toggleDiscountType refuses the amount its click asks for. It
 * says why in its title and through aria-describedby, which names `reason` only while it is locked:
 * a hidden element that aria-describedby names is still read. Whether the reason is seen is the
 * caller's: the document shows its own beside the toggle, a line keeps its own hidden.
 *
 * The lock means "EUR cannot be chosen", never "the amount is not sent": the amount input is never
 * disabled, because a disabled input posts nothing. A cleared amount would then never reach the
 * server, the amount saved on the expense would survive the save and be refused, and clearing it
 * again on the refused page would drop it again. An amount that is there (the page the server
 * refused) stays as it is, posted and refused again rather than silently dropped. Once it is
 * cleared the block goes back to %, and the blank amount is posted. An issued document has no
 * mode, so nothing is locked there.
 *
 * Anything typed counts as an amount, not only a positive one: a field being retyped ("0" on the
 * way to "0,5") must not go back to % under the user's hands.
 *
 * @param {Object} lock
 * @param {boolean} lock.gross - Whether the expense is calculated per gross
 * @param {HTMLElement|null} lock.toggle - The EUR toggle button, if the block has one
 * @param {string} lock.title - Why the toggle is locked, shown as its title
 * @param {HTMLElement|null} [lock.reason] - The element that says why, with an id, if the block has one;
 *   only named here, never shown or hidden
 * @param {boolean} lock.isAmount - Whether the block is in amount mode
 * @param {HTMLInputElement|null} lock.amountInput - The amount input, if the block has one
 * @returns {boolean} `true` when the block has to go back to % (the caller switches it and shows it)
 */
export function syncGrossAmountLock({ gross, toggle, title, reason = null, isAmount, amountInput }) {
  // A reason a refused tap brought into view goes with the lock.
  if (!gross) concealLockReason(reason)

  if (toggle) {
    if (gross) {
      toggle.setAttribute("aria-disabled", "true")
    } else {
      toggle.removeAttribute("aria-disabled")
    }

    if (gross && title) {
      toggle.title = title
    } else {
      toggle.removeAttribute("title")
    }

    if (gross && reason?.id) {
      toggle.setAttribute("aria-describedby", reason.id)
    } else {
      toggle.removeAttribute("aria-describedby")
    }
  }

  return gross && isAmount && (amountInput?.value ?? "").trim() === ""
}

// How long a refused tap on a locked EUR toggle shows its reason (revealLockReason).
export const LOCK_REASON_REVEAL_MS = 4000

// The reasons a refused tap is showing, each with the timeout that hides it again.
const revealTimers = new WeakMap()

/**
 * A tap on a locked EUR toggle is refused (the caller's toggleDiscountType returns), and a touch
 * screen shows no title: the reason the toggle names is shown beside it for a few seconds. A
 * reason that is shown already (the document's, beside its toggle) is left as it is; a repeated
 * tap shows a line's for longer. The lock going hides it at once (syncGrossAmountLock).
 * @param {HTMLElement|null} reason - The element the locked toggle names as its description
 */
export function revealLockReason(reason) {
  if (!reason || (!reason.hidden && !revealTimers.has(reason))) return

  clearTimeout(revealTimers.get(reason))
  reason.hidden = false
  revealTimers.set(
    reason,
    setTimeout(() => concealLockReason(reason), LOCK_REASON_REVEAL_MS)
  )
}

/**
 * Hides a reason revealLockReason is showing, and drops its timer; any other is left as it is.
 * @param {HTMLElement|null} reason
 */
export function concealLockReason(reason) {
  if (!reason || !revealTimers.has(reason)) return

  clearTimeout(revealTimers.get(reason))
  revealTimers.delete(reason)
  reason.hidden = true
}
